import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

export const crmUi = {
  pageBg: "#f7f8fa",
  panel: "#ffffff",
  border: "#e6e9ee",
  borderStrong: "#d9dee7",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
  soft: "#f8fafc",
  success: "#248a52",
  warning: "#d99000",
  danger: "#d92d20",
};

export const panelSx = {
  bgcolor: crmUi.panel,
  border: `1px solid ${crmUi.border}`,
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(16,24,40,.03), 0 8px 24px rgba(16,24,40,.016)",
};

export const CrmPanel = ({ children, sx, ...props }) => (
  <Paper elevation={0} sx={{ ...panelSx, ...sx }} {...props}>
    {children}
  </Paper>
);

export const CrmPageHeader = ({
  title,
  subtitle,
  actions,
  eyebrow,
}) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    spacing={1.25}
    justifyContent="space-between"
    alignItems={{ xs: "stretch", md: "flex-start" }}
    sx={{ mb: 1.45 }}
  >
    <Box sx={{ minWidth: 0 }}>
      {eyebrow && (
        <Typography
          sx={{
            mb: 0.25,
            color: "primary.main",
            fontSize: 10.5,
            fontWeight: 560,
            textTransform: "uppercase",
            letterSpacing: ".04em",
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Typography
        component="h1"
        sx={{
          color: crmUi.text,
          fontSize: { xs: 20, md: 22 },
          lineHeight: 1.2,
          fontWeight: 560,
          letterSpacing: "-0.018em",
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ mt: 0.35, color: crmUi.muted, fontSize: 12.25, lineHeight: 1.45 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {actions && (
      <Stack
        direction="row"
        spacing={0.7}
        sx={{ flexWrap: "wrap", rowGap: 0.7, justifyContent: { xs: "flex-start", md: "flex-end" } }}
      >
        {actions}
      </Stack>
    )}
  </Stack>
);

export const MetricCard = ({
  label,
  value,
  change,
  icon,
  tone = "primary",
  helper,
}) => {
  const color = tone === "success"
    ? crmUi.success
    : tone === "warning"
      ? crmUi.warning
      : tone === "danger"
        ? crmUi.danger
        : undefined;

  return (
    <CrmPanel sx={{ p: 1.5, height: "100%" }}>
      <Stack direction="row" spacing={1.05} alignItems="center">
        <Box
          sx={{
            width: 35,
            height: 35,
            borderRadius: 1.35,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            color: color || "primary.main",
            bgcolor: theme =>
              alpha(color || theme.palette.primary.main, 0.075),
            "& svg": { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: crmUi.muted, fontSize: 10.8, fontWeight: 500 }}>
            {label}
          </Typography>
          <Stack direction="row" spacing={0.6} alignItems="baseline">
            <Typography
              sx={{
                mt: 0.2,
                color: crmUi.text,
                fontSize: 21,
                fontWeight: 560,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              {value}
            </Typography>
            {change && (
              <Typography
                sx={{
                  color: change.startsWith("-") ? crmUi.danger : crmUi.success,
                  fontSize: 10.5,
                  fontWeight: 550,
                }}
              >
                {change}
              </Typography>
            )}
          </Stack>
          {helper && (
            <Typography sx={{ mt: 0.35, color: crmUi.subtle, fontSize: 9.8 }}>
              {helper}
            </Typography>
          )}
        </Box>
      </Stack>
    </CrmPanel>
  );
};

export const StatusChip = ({ status }) => {
  const warning = status === "Behöver uppföljning";
  const inactive = status === "Inaktiv";
  return (
    <Chip
      size="small"
      label={status}
      sx={{
        height: 20,
        borderRadius: 1,
        fontSize: 9.6,
        fontWeight: 520,
        color: inactive ? crmUi.muted : warning ? "#8a5a00" : crmUi.success,
        bgcolor: inactive ? "#f2f4f7" : warning ? "#fff5dc" : "#eef8f2",
        border: `1px solid ${inactive ? "#e4e7ec" : warning ? "#f0d89a" : "#cce8d7"}`,
        "& .MuiChip-label": { px: 0.7 },
      }}
    />
  );
};

export const RoleChip = ({ value }) => {
  const map = {
    "Direkt kund": { bg: "#eef8f2", color: "#247a4d", border: "#cce8d7" },
    Partner: { bg: "#eef4ff", color: "#3768a6", border: "#cfdef6" },
    Leverantör: { bg: "#fff4e8", color: "#a55b10", border: "#f2d5b3" },
    Prospekt: { bg: "#f5f1ff", color: "#7250a8", border: "#ded3f3" },
    Nätverksorganisation: { bg: "#f5f1ff", color: "#7250a8", border: "#ded3f3" },
  };
  const c = map[value] || { bg: "#f2f4f7", color: crmUi.muted, border: "#e4e7ec" };
  return (
    <Chip
      size="small"
      label={value}
      sx={{
        height: 20,
        borderRadius: 1,
        bgcolor: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        fontSize: 9.5,
        fontWeight: 520,
        "& .MuiChip-label": { px: 0.7 },
      }}
    />
  );
};

export const OwnerCell = ({ name }) => (
  <Stack direction="row" spacing={0.65} alignItems="center">
    <Avatar sx={{ width: 22, height: 22, fontSize: 9.5 }}>
      {name.split(" ").map(x => x[0]).slice(0, 2).join("")}
    </Avatar>
    <Typography sx={{ color: crmUi.text, fontSize: 11.2 }} noWrap>
      {name}
    </Typography>
  </Stack>
);

export const EmptyState = ({ title, description, action }) => (
  <CrmPanel sx={{ p: 3, textAlign: "center" }}>
    <Typography sx={{ color: crmUi.text, fontSize: 14, fontWeight: 550 }}>{title}</Typography>
    <Typography sx={{ mt: 0.5, color: crmUi.muted, fontSize: 11.5 }}>{description}</Typography>
    {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
  </CrmPanel>
);

export const SmallLinkButton = ({ children, ...props }) => (
  <Button
    size="small"
    {...props}
    sx={{
      minHeight: 30,
      px: 1,
      borderRadius: 1,
      textTransform: "none",
      fontSize: 10.8,
      fontWeight: 520,
      ...props.sx,
    }}
  >
    {children}
  </Button>
);

export const ResponsiveGrid = ({ children, ...props }) => (
  <Grid container spacing={1.15} {...props}>
    {children}
  </Grid>
);
